import { AlertTriangle, Ban, Building2, CalendarClock, Coins, Layers, Link2 } from 'lucide-react';
import type { GroupListSummary } from '../../types/customerDuplicates';
import { COMPANY_LABELS } from '../../types/customerDuplicates';

interface Props {
    summary: GroupListSummary;
    activeCompany: string;
    activeWarning: string;
    multiCompany: boolean;
    onCompanyClick: (company: string) => void;
    onWarningClick: (warning: string) => void;
    onMultiCompanyClick: () => void;
}

function eur(n: number): string {
    return Number(n ?? 0).toLocaleString('de-CH', { maximumFractionDigits: 0 });
}

function num(n: number): string {
    return Number(n ?? 0).toLocaleString('de-CH');
}

/**
 * The group view's headline numbers.
 *
 * The warning tiles are the reason this view exists, so they come first and they are counted
 * server-side with the warning filter REMOVED — click "blocked" and the "overdue" tile keeps
 * showing how many overdue splits there are rather than collapsing to 0. That is the P3 defect
 * (handoff P3 § 4.2) designed out rather than fixed after the fact.
 *
 * "Total open AR" is the number Gabi Schwarz is missing today: the open balance of every account
 * of every grouped customer, added up across the eight companies, which no other view in the app
 * can produce.
 */
export function GroupsKpiRow({
    summary, activeCompany, activeWarning, multiCompany,
    onCompanyClick, onWarningClick, onMultiCompanyClick,
}: Props) {
    const { totals, warning, config } = summary;

    const warningTiles: { key: string; label: string; value: number; hint: string; icon: typeof Ban }[] = [
        {
            key: 'ANY', label: 'Companies disagree', value: warning.anyWarning, icon: AlertTriangle,
            hint: 'One company carries this customer blocked or badly overdue while another carries it open.',
        },
        {
            key: 'BLOCKED', label: 'Blocked in one company', value: warning.blockedSplit, icon: Ban,
            hint: 'At least one company has the account blocked in the ERP; another company has an account that can still trade.',
        },
        {
            key: 'OVERDUE', label: `Overdue > ${config.overdueDays} d`, value: warning.overdueSplit, icon: CalendarClock,
            hint: `At least one company holds items ${config.overdueDays}+ days overdue worth ${eur(config.minOpenAmountEur)} EUR or more; another carries the customer clear.`,
        },
    ];

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
                {warningTiles.map(tile => {
                    const active = activeWarning === tile.key;
                    const Icon = tile.icon;
                    const alarming = tile.value > 0;
                    return (
                        <button
                            key={tile.key}
                            onClick={() => onWarningClick(active ? '' : tile.key)}
                            title={tile.hint}
                            className={`rounded border px-3 py-2 text-left text-sm transition-colors ${
                                active
                                    ? 'border-amber-500 bg-amber-50'
                                    : alarming
                                        ? 'border-amber-200 bg-white hover:bg-amber-50'
                                        : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                        >
                            <div className={`flex items-center gap-1.5 text-xs ${alarming ? 'text-amber-700' : 'text-gray-500'}`}>
                                <Icon className="h-3.5 w-3.5" /> {tile.label}
                            </div>
                            <div className={`font-semibold ${alarming ? 'text-amber-900' : 'text-gray-900'}`}>
                                {num(tile.value)}
                            </div>
                        </button>
                    );
                })}
                <button
                    onClick={onMultiCompanyClick}
                    title="Only groups whose accounts sit in more than one Dolder company."
                    className={`rounded border px-3 py-2 text-left text-sm transition-colors ${
                        multiCompany ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                >
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Building2 className="h-3.5 w-3.5" /> Across companies
                    </div>
                    <div className="font-semibold text-gray-900">{num(totals.multiCompanyGroups)}</div>
                </button>
            </div>

            <div className="flex flex-wrap gap-2">
                {Object.entries(summary.byCompany).map(([company, n]) => (
                    <button
                        key={company}
                        onClick={() => onCompanyClick(activeCompany === company ? '' : company)}
                        className={`rounded border px-2.5 py-1 text-xs transition-colors ${
                            activeCompany === company
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        {COMPANY_LABELS[company] ?? company.toUpperCase()} <span className="font-semibold">{num(n)}</span>
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <div className="rounded border border-gray-200 bg-white p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><Layers className="h-3.5 w-3.5" /> Groups</div>
                    <div className="font-semibold text-gray-900">{num(totals.groups)}</div>
                    <div className="text-xs text-gray-400">{num(totals.members)} accounts</div>
                </div>
                <div className="rounded border border-gray-200 bg-white p-3"
                    title="The open AR balance of every account of every grouped customer, added across the eight companies — the total balance that exists in no other view.">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><Coins className="h-3.5 w-3.5" /> Total open AR</div>
                    <div className="font-semibold text-gray-900">{eur(totals.openAmountEur)} EUR</div>
                    <div className="text-xs text-gray-400">{num(totals.openItems)} items</div>
                </div>
                <div className="rounded border border-gray-200 bg-white p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><CalendarClock className="h-3.5 w-3.5" /> Of that overdue</div>
                    <div className="font-semibold text-gray-900">{eur(totals.overdueAmountEur)} EUR</div>
                    <div className="text-xs text-gray-400">&gt; {config.overdueDays} days</div>
                </div>
                <div className="rounded border border-gray-200 bg-white p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><Coins className="h-3.5 w-3.5" /> Revenue ({config.revenueWindowMonths}m)</div>
                    <div className="font-semibold text-gray-900">{eur(totals.revenueEur)} EUR</div>
                    <div className="text-xs text-gray-400">{num(totals.blockedMembers)} blocked accounts</div>
                </div>
                <div className="rounded border border-gray-200 bg-white p-3"
                    title="HubSpot companies linked to these accounts via HubSpot.CustomerLinkERP. The link is 1:1, so this is how many CRM records a clean-up would touch. Read only — this app never writes to the CRM replica.">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><Link2 className="h-3.5 w-3.5" /> HubSpot records</div>
                    <div className="font-semibold text-gray-900">{num(totals.hubspotCompanies)}</div>
                    <div className="text-xs text-gray-400">read only</div>
                </div>
            </div>
        </div>
    );
}
